import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Selectors } from '@grandvisionhq/auth';
import { toast } from '@grandvisionhq/common';
import { Util } from '@grandvisionhq/forms';
import { WithUserData, isDataSuccess, isDataLoading } from '@grandvisionhq/my-account';
import { useFeatureToggles, useIntl, useDispatch, useSelector } from '@grandvisionhq/state';
import { init, initOrder } from '../actions.js';
import { shouldCreateAccount } from '../selectors.js';
import { CustomerType } from '../types.js';
import { toggleDeliveryAddress, toggleIsCompany, markFieldsTouched, setMyAccountValidation, setServiceStoreValidation } from './actions.js';
import { useFormPrefill } from './hooks/use-form-prefill.js';
import { getAddressBookPrefillValidationResult, getCompany, isShippingStepValid, isUserDataPrefilled, getSelectedShippingMethod, getServiceStore, hasStoredDeliveryAddress } from './selectors.js';
import { StepShipping } from './StepShipping.js';
import { isPrefillLoading } from './utils/is-prefill-loading.js';
import { ShippingMethod } from '../models/step-shipping/shipping-methods.js';
import { isDelivery, isDeliveryExpress } from '../utils/shipping-method.js';
import { useCheckoutSettings } from '../context/CheckoutSettings/context.js';

const StepShippingContainer = ({ onNextStep , onPrevStep , shippingMethodsAsides , userData ,  })=>{
    var ref, ref1, ref2, ref3;
    const checkoutSettings = useCheckoutSettings();
    const { isAddressBookEnabled , isMyAccountEnabled  } = useFeatureToggles();
    const { getLabel  } = useIntl();
    const dispatch = useDispatch();
    const addressBookPrefillValidationResult = useSelector(getAddressBookPrefillValidationResult);
    const company = useSelector(getCompany);
    const isLoggedIn = useSelector(Selectors.isLoggedIn);
    const isShippingStepValidBoolean = useSelector(isShippingStepValid);
    const isUserDataPrefilledBoolean = useSelector(isUserDataPrefilled);
    const selectedShippingMethod = useSelector(getSelectedShippingMethod);
    const selectedServiceStore = useSelector(getServiceStore);
    const shouldCreateAccountBoolean = useSelector(shouldCreateAccount);
    const hasStoredDeliveryAddressBoolean = useSelector(hasStoredDeliveryAddress);
    const isCreateAccountRequired = Boolean(((ref = checkoutSettings.account) === null || ref === void 0 ? void 0 : ref.implicitAccountCreation) || ((ref1 = checkoutSettings.account) === null || ref1 === void 0 ? void 0 : ref1.createAccountRequired));
    const [isOptInsFetching, setIsOptInsFetching] = React.useState(true);
    useFormPrefill(isDataSuccess(userData) ? userData : undefined);
    const isCompanyEnabled = (ref2 = checkoutSettings.features) === null || ref2 === void 0 ? void 0 : ref2.isCompanyEnabled;
    const isServiceStoreEnabled = (ref3 = checkoutSettings.features) === null || ref3 === void 0 ? void 0 : ref3.isServiceStoreEnabled;
    const isStepValid = ()=>{
        return isShippingStepValidBoolean && (!isServiceStoreEnabled || !!selectedServiceStore) && (isLoggedIn || !isCreateAccountRequired || shouldCreateAccountBoolean);
    };
    const onToggleIsCompany = (radio)=>{
        dispatch(toggleIsCompany({
            enabled: radio.value === CustomerType.Company
        }));
    };
    const onToggleDeliveryAddress = ({ checked  })=>{
        dispatch(toggleDeliveryAddress({
            enabled: checked
        }));
    };
    React.useEffect(()=>{
        dispatch(init());
        dispatch(initOrder());
    }, [
        dispatch
    ]);
    React.useEffect(()=>{
        /**
     * To ensure that the delivery address is prefilled, it is necessary to  enable the
     * delivery form in advance. However, it is important to note that this requirement
     * is applicable only to the "Delivery" and "DeliveryExpress" shippingMethod options.
     */ if (hasStoredDeliveryAddressBoolean && ((selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method) === ShippingMethod.Delivery || (selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method) === ShippingMethod.DeliveryExpress)) {
            dispatch(toggleDeliveryAddress({
                enabled: true
            }));
        }
    /**
     *  This hook should only be executed during the on mount process.
     */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const nextStep = ()=>{
        if (!isStepValid()) {
            dispatch(markFieldsTouched());
            if (isCreateAccountRequired && !shouldCreateAccountBoolean) {
                dispatch(setMyAccountValidation());
            }
            if (isServiceStoreEnabled) {
                dispatch(setServiceStoreValidation());
            }
            toast.failed('checkout.errors.form.general');
            Util.focusToTopErrorFieldOnScreen();
        } else {
            onNextStep();
        }
    };
    /**
   *  Callback from OptInContainer to return
   *  loading state of OptIns from GraphQL call
   *
   *  @param isOptInsLoading
   *    flag to return loading state of OptIns
   */ const handleOptInsLoading = React.useCallback(({ isOptInsLoading  })=>{
        setIsOptInsFetching(isOptInsLoading);
    }, []);
    const showLoader = isPrefillLoading({
        userDataLoading: isDataLoading(userData),
        isUserDataPrefilledBoolean,
        isAddressBookEnabled,
        addressBookPrefillValidationResult,
        isLoggedIn,
        isMyAccountEnabled,
        optInsDataLoading: isOptInsFetching
    });
    const labels = React.useMemo(()=>({
            generic: {
                title: getLabel('checkout.generic.title')
            },
            customerType: {
                private: getLabel('checkout.customerType.private'),
                company: getLabel('checkout.customerType.company')
            },
            shipmentMethodTitle: getLabel('checkout.shipment.method.title'),
            serviceStoreTitle: getLabel('checkout.serviceStore.title'),
            serviceStoreSubtitle: getLabel('checkout.serviceStore.subtitle')
        })
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React.createElement(StepShipping, {
        company: Boolean(company),
        isCompanyEnabled: Boolean(isCompanyEnabled),
        isDelivery: isDelivery(selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method),
        isDeliveryExpress: isDeliveryExpress(selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method),
        labels: labels,
        onNextStep: nextStep,
        onPrevStep: onPrevStep,
        routes: checkoutSettings === null || checkoutSettings === void 0 ? void 0 : checkoutSettings.routes,
        shippingMethodsAsides: shippingMethodsAsides,
        showLoader: showLoader,
        toggleDeliveryAddress: onToggleDeliveryAddress,
        toggleIsCompany: onToggleIsCompany,
        handleOptInsLoading: handleOptInsLoading
    });
};
const StepShippingContainerWithUserData = WithUserData(StepShippingContainer);
const StepShippingContainerWithMyAccount = (_param)=>{
    var props = swcHelpers.extends({}, _param);
    const { isMyAccountEnabled  } = useFeatureToggles();
    return /*#__PURE__*/ React.createElement(StepShippingContainerWithUserData, swcHelpers.extends({}, props, {
        isMyAccountEnabled: isMyAccountEnabled
    }));
};
var StepShippingContainerWithMyAccount$1 = StepShippingContainerWithMyAccount;

export { StepShippingContainerWithMyAccount$1 as default };
//# sourceMappingURL=StepShippingContainer.js.map
